import { create } from 'zustand';

import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import type {
  CreateQuotePayload,
  Quote,
  QuotesList,
} from '@/store/expenses-store/quotes/quote.dto.ts';
import { downloadBlob } from '@/utils/download-file-from-link.ts';
import { generateSearchParams } from '@/utils/generate-search-params.ts';
import { toast } from '@/utils/toast';

type QuotesStoreProps = {
  quotes: QuotesList | null;
  quote: Quote | null;
  loading: boolean;
  exportLoading: boolean;
  error: string | null;
  getQuotes: (options?: { taskId?: string }) => Promise<void>;
  getQuote: (quoteId: string) => Promise<void>;
  exportQuotes: () => Promise<void>;
  createQuote: (body: CreateQuotePayload) => Promise<void>;
  updateQuote: (quoteId: string, body: CreateQuotePayload) => Promise<void>;
  setQuoteStatus: (quoteId: string, status: string) => Promise<void>;
  removeQuote: (quoteId: string) => Promise<void>;
};

export const useQuotesStore = create<QuotesStoreProps>((set, get) => ({
  quotes: null,
  quote: null,
  loading: false,
  exportLoading: false,
  error: null,
  getQuotes: async (options) => {
    set({ loading: true, error: null });
    try {
      const searchParams = generateSearchParams({
        limit: 1000,
        page: 1,
        task_id: options?.taskId,
      });
      const response = await getRequest<QuotesList>(API.EXPENSES, `/quote?${searchParams}`);
      set({ quotes: response.data, loading: false, error: null });
    } catch {
      toast.error('Couldn’t load the quotes. Please try again.');
    } finally {
      set({ loading: false });
    }
  },
  getQuote: async (quoteId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest<Quote>(API.EXPENSES, `/quote/${quoteId}`);
      set({ quote: response.data, loading: false, error: null });
    } catch {
      toast.error('Couldn’t load the quote. Please try again.');
    } finally {
      set({ loading: false });
    }
  },
  exportQuotes: async () => {
    if (get().exportLoading) return;
    set({ exportLoading: true, error: null });
    try {
      const blob = await getRequestBlob(API.EXPENSES, '/quote/export');
      await downloadBlob(blob, `quotes-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'error fetching quotes';
      set({ error: message });
    } finally {
      set({ exportLoading: false });
    }
  },
  createQuote: async (body: CreateQuotePayload) => {
    set({ error: null });
    try {
      const response = await postRequest<Quote, CreateQuotePayload>(API.EXPENSES, '/quote', body);
      set((state) => ({
        quote: response.data,
        quotes: state.quotes
          ? { ...state.quotes, items: [response.data, ...state.quotes.items] }
          : state.quotes,
        error: null,
      }));
      toast.success('Quote created');
      void get().getQuotes();
    } catch {
      toast.error('Couldn’t create the quote. Please check the details.');
    }
  },
  updateQuote: async (quoteId: string, body: CreateQuotePayload) => {
    set({ error: null });
    try {
      const response = await putRequest<Quote, CreateQuotePayload>(
        API.EXPENSES,
        `/quote/${quoteId}`,
        body,
      );
      set((state) => ({
        quote: response.data,
        quotes: state.quotes
          ? {
              ...state.quotes,
              items: state.quotes.items.map((quote) =>
                String(quote.id) === String(quoteId) ? response.data : quote,
              ),
            }
          : state.quotes,
        error: null,
      }));

      toast.success('Quote updated');
      void get().getQuotes();
    } catch {
      toast.error('Couldn’t update the quote. Please try again.');
    }
  },
  setQuoteStatus: async (quoteId: string, status: string) => {
    set({ error: null });
    try {
      const response = await patchRequest<Quote, { status: string }>(
        API.EXPENSES,
        `/quote/${quoteId}`,
        { status },
      );
      set((state) => ({
        quote: response.data,
        quotes: state.quotes
          ? {
              ...state.quotes,
              items: state.quotes.items.map((quote) =>
                String(quote.id) === String(quoteId) ? response.data : quote,
              ),
            }
          : state.quotes,
        error: null,
      }));

      toast.success('Quote status updated');
      void get().getQuotes();
    } catch {
      toast.error('Couldn’t update the quote status. Please try again.');
    }
  },
  removeQuote: async (quoteId: string) => {
    set({ error: null });
    try {
      await deleteRequest(API.EXPENSES, `/quote/${quoteId}`);
      set((state) => ({
        quote: state.quote?.id === quoteId ? null : state.quote,
        quotes: state.quotes
          ? {
              ...state.quotes,
              items: state.quotes.items.filter((quote) => String(quote.id) !== String(quoteId)),
            }
          : state.quotes,
        error: null,
      }));

      toast.success('Quote deleted');
      void get().getQuotes();
    } catch {
      toast.error('Couldn’t delete the quote. Please try again.');
    }
  },
}));
